import React, { FC, useCallback } from 'react';
import { BasicButton } from 'src/components/widgets/buttons';
import { T } from 'src/shared/helpers';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import styled from 'styled-components';
import { Dialog, useDialogManager } from '../../../../widgets/dialog';
import { DialogContent, DialogFooter } from '../shared/sharedStyles';

const NO_CREDITS_DIALOG_ID = 'ai-studio-no-credits';

const NoCreditsMessage = styled.p`
    margin: 0;
    max-width: 420px;
    line-height: 1.5;
`;

interface AIStudioNoCreditsDialogProps {
    onClose: () => void;
    /** Dialog heading. Defaults to the generic out-of-credits copy; callers can override it to fit the context. */
    title?: string;
}

/**
 * Shown when the backend reports NO_CREDITS. With the AI credit model there are no credit packs to buy: every
 * account gets a one-time amount of free AI credits, and only a trial or a Shopify development store is stopped
 * when they are used up (a paying seller keeps using the tools and pays for them). The dialog says so.
 * The calls to action (plan upgrade, switch from a development store to a paying store) will be added later.
 */
const AIStudioNoCreditsDialog: FC<AIStudioNoCreditsDialogProps> = ({ onClose, title }) => {
    const { ecommerceName, shopifyEcommerceSettings } = useSubscriptionData();
    // Same rule as the backend: a Shopify development store in every environment.
    const isShopifyDevStore = ecommerceName === 'SHOPIFY' && shopifyEcommerceSettings?.isTestStore === true;

    return (
        <Dialog title={title ?? T._('Free AI credits used up', 'Admin')} onClose={onClose}>
            <DialogContent>
                <NoCreditsMessage>
                    {isShopifyDevStore
                        ? T._('Your store is a development store and its free AI credits are used up.', 'Admin')
                        : T._('You are in your trial period and your free AI credits are used up.', 'Admin')}
                </NoCreditsMessage>
                <DialogFooter>
                    <BasicButton onClick={onClose}>{T._('Close', 'Admin')}</BasicButton>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

/** Returns a stable callback that opens the AI Studio no-credits dialog with an optional custom title. */
export function useAIStudioCreditsDialog(): { showNoCreditsDialog: (title?: string) => void } {
    const { showDialog, closeDialog } = useDialogManager();

    const showNoCreditsDialog = useCallback(
        (title?: string) => {
            showDialog(NO_CREDITS_DIALOG_ID, <AIStudioNoCreditsDialog title={title} onClose={() => closeDialog(NO_CREDITS_DIALOG_ID)} />);
        },
        [showDialog, closeDialog]
    );

    return { showNoCreditsDialog };
}
